import { useEffect, useState, useCallback, useRef } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { Option } from "components/select/Select";

interface UseDependentSelectProps {
  form: UseFormReturn<any>;
  dependOn: string;
  targetField: string;
  fetchData: (dependentValue: any) => Promise<Option[]>;
  clearTargetOnDependencyChange?: boolean;
}

export function useDependentSelect({
  form,
  dependOn,
  targetField,
  fetchData,
  clearTargetOnDependencyChange = true,
}: UseDependentSelectProps) {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dependentValue = useWatch({
    control: form.control,
    name: dependOn,
  });

  const previousValue = useRef<any>(undefined);

  const loadOptions = useCallback(
    async (value: any) => {
      if (!value) {
        setOptions([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await fetchData(value);
        setOptions(result);
      } catch (err) {
        console.error(err);
        setError("error in api");
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchData]
  );

  useEffect(() => {
    if (!dependentValue) {
      setOptions([]);
      previousValue.current = dependentValue;
      return;
    }

    if (previousValue.current === undefined) {
      previousValue.current = dependentValue;
      loadOptions(dependentValue);
      return;
    }

    if (previousValue.current !== dependentValue) {
      previousValue.current = dependentValue;

      if (clearTargetOnDependencyChange) {
        form.setValue(targetField as any, null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      loadOptions(dependentValue);
    }
  }, [
    dependentValue,
    form,
    targetField,
    clearTargetOnDependencyChange,
    loadOptions,
  ]);

  const reload = useCallback(() => {
    const value = form.getValues(dependOn);
    if (value) {
      loadOptions(value);
    }
  }, [form, dependOn, loadOptions]);

  return {
    options,
    loading,
    error,
    reload,
  };
}
